import { AxiosResponse } from "axios";
import { RestUrl } from "@/commons/constants/RestUrl";
import CommonService from "@/commons/services/CommonService";
import RequestHeaderUtil from "@/commons/utils/RequestHeader";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { FacilityGetModel } from "@/commons/services/Facilities";

/**
 * ### Authorization API 操作クラス
 *
 * - ログイン、ログアウト、トークン更新を行う
 */
export default class Authorization {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### Login
   *
   * - ユーザーIDとパスワードでログインAPIにリクエストを送る
   * - ログインはトークン切れ等が無いので、親クラスのexecuteメソッドは使用しない
   */
  public async login(requestBody: LoginPostModel): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await window.axios.post(RestUrl.Login, JSON.stringify(requestBody), {
      headers: RequestHeaderUtil.createHeader()
    });
    this.commonService.checkStatusCode(null, response, this.constructor.name, "login", requestBody, HitomeqErrorType.REQUEST_RETRY);
    return response.data;
  }

  /**
   * ### LoginByFacility
   *
   * - ユーザーIDとパスワードでログインAPIにリクエストを送る
   * - ログインはトークン切れ等が無いので、親クラスのexecuteメソッドは使用しない
   */
  public async loginByFacility(requestBody: LoginByFacilityPostModel): Promise<LoginByFacilityResponse> {
    const response: AxiosResponse<LoginByFacilityResponse> = await window.axios.post(RestUrl.LoginByFacility, JSON.stringify(requestBody), {
      headers: RequestHeaderUtil.createHeader()
    });
    this.commonService.checkStatusCode(null, response, this.constructor.name, "loginByFacility", requestBody, HitomeqErrorType.REQUEST_RETRY);
    return response.data;
  }

  /**
   * ### Logout
   *
   * - ログアウト要求をAPIに送る
   * - ログアウトはトークン切れ等が無いので、親クラスのexecuteメソッドは使用しない
   */
  public async logout(userId: string): Promise<void> {
    const requestBody: any = {
      userId: userId
    };

    try {
      await window.axios.post(RestUrl.Logout, JSON.stringify(requestBody), {
        headers: RequestHeaderUtil.createHeader()
      });
    } catch (error) {
      // ログアウトに失敗しても何もしない
    }

    return;
  }
}

export interface LoginPostModel {
  /** ユーザーID */
  userId: string;
  /** パスワード */
  password: string;
}

/**
 * ログイン時のレスポンスボディ
 */
export interface LoginResponse {
  /** 施設 */
  facilities: Array<FacilityGetModel>;
}

/**
 * ログインユーザー情報
 */
export interface Account {
  /** ユーザーID */
  userId: string;

  /** ユーザー氏名 */
  name: string;
  /** ユーザ氏名(かな) */
  kana: string;
  /** 電話番号 */
  tel: string;
  /** メールアドレス */
  mail: string;
  /** 確認済みお知らせIDリスト */
  confirmedNotificationIds: Array<number>;
  /** お知らせメール配信の受信可否 */
  receiveNotification: boolean;
  /** 加入日 */
  started: number;

  /** 施設ID */
  facilityId: number;

  /** 権限 */
  role: Role;
  /** 権限 */
  permissions: Permissions;
  /** 最終リクエスト時間 */
  latestRequestDatetime: number;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 作成日 */
  created: number;
  /** 更新日 */
  updated: number;
}

export interface Permissions {
  /** 閲覧可能利用者グループID */
  residentGroups: number[];
  /** 閲覧可能施設関係者グループID */
  otherUserGroups: number[];
  /** 閲覧可能メッセージ記載者種別。0:全て、1：自分のみ */
  showMessages: ShowMessage;

  /** 機能の使用権限 */
  functions: Functions;

  /** メッセージ送信機能の詳細権限 */
  sendMessagesFunction: SendMessagesFunction;
  /** 面会予約機能の詳細権限 */
  receiveMessagesFunction: ReceiveMessagesFunction;
  /** 受信メッセージ機能の詳細権限 */
  reservationsFunction: ReservationsFunction;
  /** 待機者受信メッセージ機能の詳細権限 */
  waiterReceiveMessagesFunction: WaiterReceiveMessagesFunction;
  /** 文書送付の詳細権限 */
  sendDocumentsFunction: SendDocumentsFunction;
}

export enum ShowMessage {
  UNKNOWN = -1,
  ALL = 0,
  ONLY_MINE = 1
}

export enum Role {
  KM_USER = 0,
  ADMIN_USER = 1,
  STANDARD_USER = 2
}

/**
 * ### ユーザー毎機能使用可否
 *
 * - 施設アプリのサイドメニューと対応する
 */
export interface Functions {
  /**
   * 家族連絡サービス
   */
  /** 施設情報（修正） */
  facilitiesFunction: boolean;
  /** 部屋情報（修正等） */
  roomsFunction: boolean;
  /** 家族の使用機能（変更） */
  useFamilyFunctionsFunction: boolean;
  /** 家族の使用機能（個別設定） */
  individuallyUseFamilyFunctionsFunction: boolean;
  /** 利用者 */
  residentsFunction: boolean;
  /** 家族ユーザー */
  familiesFunction: boolean;
  /** 施設関係者 */
  otherUserGroupsFunction: boolean;
  /** 施設関係者の使用機能（変更） */
  useOtherUserFunctionsFunction: boolean;
  /** 宛先グループ（修正等） */
  sendMessageGroupsFunction: boolean;
  /** 介護記録 */
  careimagesFunction: boolean;
  /** 写真印刷依頼 */
  picturesFunction: boolean;
  /** イベント情報 */
  eventsFunction: boolean;
  /** 面会予約 */
  reservationsFunction: boolean;
  /** 外出外泊届け */
  outingsFunction: boolean;
  /** メッセージ送信 */
  sendMessagesFunction: boolean;
  /** 文書送付 */
  sendDocumentsFunction: boolean;
  /** 受信メッセージ */
  receiveMessagesFunction: boolean;
  /** 家族の使用状況 */
  familyMessagesFunction: boolean;
  /** アンケート */
  questionnairesFunction: boolean;
  /** 安否確認 */
  safetyConfirmationFunction: boolean;
  /** 空床スケジュール */
  roomSchedulesFunction: boolean;

  /**
   * 待機者サービス
   */
  /** 入所者募集管理を使用するか */
  useWaiterService: boolean;
  /** 入所申込者管理 */
  waitersFunction: boolean;
  /** メッセージ送信 */
  waiterSendMessagesFunction: boolean;
  /** フォロワー */
  waiterFollowersFunction: boolean;
  /** 施設見学 */
  inspectionReservationsFunction: boolean;
  /** お問い合わせ */
  recruitmentContactsFunction: boolean;
  /** 受信メッセージ(待機者) */
  waiterReceiveMessagesFunction: boolean;

  /**
   * 家族連絡、待機者サービス共通
   */
  /** 活動履歴 */
  reportsFunction: boolean;

  /**
   * 他事業所連携サービス
   */
  /** 他事業所連携を使用するか */
  useOtherOfficeConnectionService: boolean;
  /** トップ */
  otherOfficeTopPageFunction: boolean;
  /** メッセージ */
  otherOfficeMessagesFunction: boolean;
  /** 他事業所連携 オンライン会議 */
  otherOfficeOnlineMeetingsFunction: boolean;
  /** 空床情報 */
  otherOfficeRoomSchedulesFunction: boolean;
  /** 他事業所連携 緊急搬送 */
  otherOfficeEmergencyTransportsFunction: boolean;
}

/**
 * ### ユーザー毎機能使用可否のプロパティ名と画面表示名のMAP
 */
export const functionNameMap: Map<string, string> = new Map([
  // 家族連絡サービス
  ["facilitiesFunction", "施設情報（修正）"],
  ["roomsFunction", "部屋情報（修正等）"],
  ["useFamilyFunctionsFunction", "家族の使用機能（変更）"],
  ["individuallyUseFamilyFunctionsFunction", "家族の使用機能（個別設定）"],
  ["useOtherUserFunctionsFunction", "施設関係者の使用機能（変更）"],
  ["residentsFunction", "利用者"],
  ["familiesFunction", "家族ユーザー"],
  ["otherUserGroupsFunction", "施設関係者"],
  ["sendMessageGroupsFunction", "宛先グループ（修正等）"],
  ["careimagesFunction", "介護記録"],
  ["picturesFunction", "写真印刷依頼"],
  ["eventsFunction", "イベント情報"],
  ["reservationsFunction", "面会予約"],
  ["outingsFunction", "外出外泊届け"],
  ["sendMessagesFunction", "メッセージ送信"],
  ["sendDocumentsFunction", "文書送付"],
  ["receiveMessagesFunction", "受信メッセージ"],
  ["familyMessagesFunction", "家族の使用状況"],
  ["questionnairesFunction", "アンケート"],
  ["safetyConfirmationFunction", "安否確認"],
  ["roomSchedulesFunction", "空床スケジュール"],
  // 入所者募集管理サービス
  ["useWaiterService", "入所者募集管理サービス"],
  ["waitersFunction", "入所申込者管理"],
  ["waiterSendMessagesFunction", "メッセージ送信"],
  ["waiterReceiveMessagesFunction", "受信メッセージ"],
  ["waiterFollowersFunction", "フォロワー"],
  ["inspectionReservationsFunction", "施設見学"],
  ["recruitmentContactsFunction", "お問い合わせ"],
  // 共通
  ["reportsFunction", "カレンダー・履歴"],
  // 他事業所連携サービス
  ["useOtherOfficeConnectionService", "他事業所連携サービス"],
  ["otherOfficeTopPageFunction", "トップ（招待・操作）"],
  ["otherOfficeMessagesFunction", "メッセージ"],
  ["otherOfficeOnlineMeetingsFunction", "オンライン会議"],
  ["otherOfficeRoomSchedulesFunction", "空床情報"],
  ["otherOfficeEmergencyTransportsFunction", "救急搬送"]
]);

/**
 * ### ユーザー毎の機能使用可否、メッセージ送信の詳細設定
 */
export interface SendMessagesFunction {
  /** 基本設定の定使用可否 */
  useBasicSetting: boolean;
  /** テンプレートの使用可否 */
  useTemplate: boolean;
  /** 一時保存の使用可否 */
  useSaveDraftMessage: boolean;
  /** 新規送信の「送信」、メッセージ詳細の返信リストの「送信」の使用可否 */
  useSendAndReply: boolean;
  /** 宛先種別の「家族全員」の使用可否 */
  useSendTargetToAllFamily: boolean;
  /** 宛先種別の「家族個別」の使用可否 */
  useSendTargetToFamily: boolean;
  /** 宛先種別の「入所申込者全員」の使用可否 */
  useSendTargetToAllWaiter: boolean;
  /** 宛先種別の「入所申込者個別」の使用可否 */
  useSendTargetToWaiter: boolean;
  /** 宛先種別の「施設関係者個別」の使用可否 */
  useSendTargetToOtherUser: boolean;
  /** 宛先種別のグループ「宛先グループ（家族）」の使用可否 */
  useSendTargetToFamilySendMessageGroup: boolean;
  /** 宛先種別のグループ「宛先グループ（施設関係者）」の使用可否 */
  useSendTargetToOtherUserSendMessageGroup: boolean;
  /** 宛先種別のグループ「利用者グループ」の使用可否 */
  useSendTargetToResidentGroup: boolean;
  /** 宛先種別のグループ「施設関係者グループ」の使用可否 */
  useSendTargetToOtherUserGroup: boolean;
}

/**
 * ### ユーザー毎の機能使用可否、メッセージ送信の詳細設定のプロパティ名と画面表示名のMAP
 */
export const sendMessagesFunctionNameMap: Map<string, string> = new Map([
  ["useBasicSetting", "基本設定"],
  ["useTemplate", "テンプレート"],
  ["useSaveDraftMessage", "下書き保存・承認依頼"],
  ["useSendAndReply", "送信・返信"],
  // 家族連絡サービス
  ["useSendTargetToAllFamily", "宛先種別（家族全員）"],
  ["useSendTargetToFamily", "宛先種別（家族個別）"],
  ["useSendTargetToOtherUser", "宛先種別（施設関係者個別）"],
  ["useSendTargetToFamilySendMessageGroup", "宛先種別（グループ：宛先グループ（家族））"],
  ["useSendTargetToOtherUserSendMessageGroup", "宛先種別（グループ：宛先グループ（施設関係者））"],
  ["useSendTargetToResidentGroup", "宛先種別（グループ：利用者グループ）"],
  ["useSendTargetToOtherUserGroup", "宛先種別（グループ：施設関係者グループ）"],
  // 待機者管理サービス
  ["useSendTargetToAllWaiter", "宛先種別（入所申込者全員）"],
  ["useSendTargetToWaiter", "宛先種別（入所申込者個別）"]
]);

/**
 * ### ユーザー毎の機能使用可否、面会予約の詳細設定
 */
export interface ReservationsFunction {
  /** 訪問面会の基本設定使用可否 */
  useReservationSettings: boolean;
  /** 訪問面会スケジュールの使用可否 */
  useReservationSchedules: boolean;
  /** オンライン面会の基本設定使用可否 */
  useOnlineReservationSettings: boolean;
  /** オンライン面会スケジュールの使用可否 */
  useOnlineReservationSchedules: boolean;
  /** 代理予約の使用可否 */
  useReserveInstead: boolean;
  /** 承諾/辞退/キャンセル/案内転送の使用可否 */
  useEditReservations: boolean;
  /** 面会開始の使用可否 */
  useStartOnlineReservations: boolean;
}

/**
 * ### ユーザー毎の機能使用可否、面会予約の詳細設定のプロパティ名と画面表示名のMAP
 */
export const reservationsFunctionNameMap: Map<string, string> = new Map([
  ["useReservationSettings", "訪問面会の基本設定"],
  ["useReservationSchedules", "訪問面会スケジュール"],
  ["useOnlineReservationSettings", "オンライン面会の基本設定"],
  ["useOnlineReservationSchedules", "オンライン面会スケジュール"],
  ["useReserveInstead", "代理予約"],
  ["useEditReservations", "承諾／辞退／キャンセル／案内転送"],
  ["useStartOnlineReservations", "面会開始"]
]);

export interface ReceiveMessagesFunction {
  /** 履歴（家族）閲覧 */
  showFamilyMessages: boolean;
  /** 履歴（施設関係者）閲覧 */
  showOtherUserMessages: boolean;
  /** 返信（家族） */
  useFamilyReply: boolean;
  /** 返信（施設関係者） */
  useOtherUserReply: boolean;
}

/**
 * ### ユーザー毎の機能使用可否、受信メッセージの詳細設定のプロパティ名と画面表示名のMAP
 */
export const receiveMessagesFunctionNameMap: Map<string, string> = new Map([
  ["showFamilyMessages", "履歴（家族）閲覧"],
  ["showOtherUserMessages", "履歴（施設関係者）閲覧"],
  ["useFamilyReply", "返信（家族）"],
  ["useOtherUserReply", "返信（施設関係者）"]
]);

export interface WaiterReceiveMessagesFunction {
  /** 履歴閲覧 */
  showMessages: boolean;
  /** 返信 */
  useReply: boolean;
}

/**
 * ### ユーザー毎の機能使用可否、受信メッセージの詳細設定のプロパティ名と画面表示名のMAP
 */
export const waiterReceiveMessagesFunctionNameMap: Map<string, string> = new Map([
  ["showMessages", "履歴閲覧"],
  ["useReply", "返信"]
]);

export interface SendDocumentsFunction {
  /** 文書種類の編集 */
  useEdit: boolean;
  /** テンプレート */
  useTemplate: boolean;
  /** 一時保存 */
  useDraft: boolean;
  /** 送信・公開・非公開・削除 */
  useControl: boolean;
  /** 宛先種別（家族全員） */
  showTargetToAllFamily: boolean;
  /** 宛先種別（家族個別） */
  showTargetToFamily: boolean;
  /** 宛先種別（施設関係者個別） */
  showTargetToOtherUser: boolean;
  /** 宛先種別のグループ「宛先グループ（家族）」の使用可否 */
  showTargetToFamilySendMessageGroup: boolean;
  /** 宛先種別のグループ「宛先グループ（施設関係者）」の使用可否 */
  showTargetToOtherUserSendMessageGroup: boolean;
  /** 宛先種別のグループ「利用者グループ」の使用可否 */
  showTargetToResidentGroup: boolean;
  /** 宛先種別のグループ「施設関係者グループ」の使用可否 */
  showTargetToOtherUserGroup: boolean;
}

/**
 * ### ユーザー毎の機能使用可否、文書送付の詳細設定のプロパティ名と画面表示名のMAP
 */
export const sendDocumentsFunctionNameMap: Map<string, string> = new Map([
  ["useEdit", "文書種類の編集"],
  ["useTemplate", "テンプレート"],
  ["useDraft", "一時保存"],
  ["useControl", "送信・公開・非公開・削除"],
  ["showTargetToAllFamily", "宛先種別（家族全員）"],
  ["showTargetToFamily", "宛先種別（家族個別）"],
  ["showTargetToOtherUser", "宛先種別（施設関係者個別）"],
  ["showTargetToFamilySendMessageGroup", "宛先種別（グループ：宛先グループ（家族））"],
  ["showTargetToOtherUserSendMessageGroup", "宛先種別（グループ：宛先グループ（施設関係者））"],
  ["showTargetToResidentGroup", "宛先種別（グループ：利用者グループ）"],
  ["showTargetToOtherUserGroup", "宛先種別（グループ：施設関係者グループ）"]
]);

/**
 * cognito認証情報
 */
export interface AuthorizationModel {
  /** 認証トークン */
  accessToken: string;
  /** 更新トークン */
  refreshToken: string;
  /** 認証トークン有効期限 */
  expiration: string;
}

/**
 * S3認証情報
 */
export interface CredentialsModel {
  /** アクセスキー */
  accessKeyId: string;
  /** 非公開アクセスキー */
  secretAccessKey: string;
  /** アクセスキー、非公開アクセスキーで共通の有効期限 */
  expiration: string;
}

export interface LoginByFacilityPostModel {
  /** ユーザーID */
  userId: string;
  /** パスワード */
  password: string;
  /** 施設ID */
  facilityId: number;
}

export interface LoginByFacilityResponse {
  /** ログインユーザー情報 */
  account: Account;
  /** 施設 */
  facility: FacilityGetModel;
  /** cognito認証情報 */
  authorization: AuthorizationModel;
  /** S3認証情報 */
  credentials: CredentialsModel;
}
